import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Anchor,
  LayoutDashboard,
  Users as UsersIcon,
  ShieldCheck,
  History,
  Database,
  Ship,
  CalendarClock,
  FileBarChart,
  FileSignature,
  FileCog,
  Kanban,
  ClipboardCheck,
  UserRoundSearch,
  ClipboardList,
  Menu,
  X,
  LogOut,
  User as UserIcon,
  FileText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DemoGuard, DemoBadge } from "@/components/DemoGuard";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Layout({ children }) {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const roleName = user?.role?.role_name;
  const pipelineRole = user?.role?.domain === "Sourcing" || ["Documentation/Operations", "Super Admin", "Office Admin", "Technical Superintendent/Assessment", "Management/HOD", "Travel Desk"].includes(roleName);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const nav = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", show: true },
    { to: "/vessels", label: "Vessels", icon: Ship, testid: "nav-vessels-link", show: hasPermission("vessels.view") },
    { to: "/daily-report", label: "Daily Report", icon: FileBarChart, testid: "nav-daily-report-link", show: true },
    { to: "/expiry-board", label: "Expiry Board", icon: CalendarClock, testid: "nav-expiry-board-link", show: hasPermission("documents.view") },
    { to: "/sea-contracts", label: "SEA Contracts", icon: FileSignature, testid: "nav-sea-contracts-link", show: hasPermission("contracts.view") },
    { to: "/pipeline", label: "Candidate Pipeline", icon: Kanban, testid: "nav-pipeline-link", show: pipelineRole },
    { to: "/candidates", label: "Candidates", icon: UserRoundSearch, testid: "nav-candidates-link", show: pipelineRole },
    { to: "/docs-queue", label: "Docs Verification", icon: ClipboardCheck, testid: "nav-docs-queue-link", show: ["Documentation/Operations", "Super Admin", "Office Admin", "Technical Superintendent/Assessment", "Management/HOD"].includes(roleName) },
    { to: "/requirements", label: "Crew Requirements", icon: ClipboardList, testid: "nav-requirements-link", show: pipelineRole },
    { to: "/proposals", label: "Proposals", icon: FileText, testid: "nav-proposals-link", show: pipelineRole },
    { to: "/users", label: "Users", icon: UsersIcon, testid: "nav-users-link", show: hasPermission("users.view") },
    { to: "/roles", label: "Roles & Permissions", icon: ShieldCheck, testid: "nav-roles-link", show: hasPermission("roles.view") },
    { to: "/audit-logs", label: "Audit Logs", icon: History, testid: "nav-audit-logs-link", show: hasPermission("audit.view") },
    { to: "/master-data", label: "Master Data", icon: Database, testid: "nav-master-data-link", show: hasPermission("master_data.view") },
    { to: "/settings/sea-templates", label: "SEA Templates", icon: FileCog, testid: "nav-sea-templates-link", show: ["Super Admin", "Management/HOD"].includes(user?.role?.role_name), section: "Settings" },
  ];

  const initials = `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase();

  return (
    <div className="min-h-screen flex bg-slate-50">
      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/50 lg:hidden" onClick={() => setMenuOpen(false)} data-testid="mobile-menu-backdrop" />
      )}
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        data-open={menuOpen}
        className={`crew-sidebar w-64 fixed left-0 top-0 bottom-0 z-50 flex flex-col border-r border-slate-800 transition-transform duration-200 lg:translate-x-0 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-800">
          <div className="w-9 h-9 rounded-md bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
            <Anchor className="w-5 h-5 text-cyan-400" />
          </div>
          <div className="leading-tight">
            <div className="text-white font-bold tracking-tight text-lg" style={{ fontFamily: "Manrope" }}>CrewOn</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Manning ERP</div>
          </div>
          <button onClick={() => setMenuOpen(false)} className="ml-auto lg:hidden text-slate-400 hover:text-white transition-colors" data-testid="mobile-menu-close-btn" aria-label="Close menu">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {nav.filter((n) => n.show).map((n) => (
            <div key={n.to}>
            {n.section && <div className="px-3 pt-4 pb-1.5 text-[10px] uppercase tracking-widest text-slate-500" data-testid="nav-section-settings">{n.section}</div>}
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `crew-nav-link ${isActive ? "" : ""}`
              }
            >
              {({ isActive }) => (
                <span data-active={isActive} className="crew-nav-link w-full">
                  <n.icon className="w-4 h-4" />
                  {n.label}
                </span>
              )}
            </NavLink>
            </div>
          ))}
        </nav>

        <div className="px-5 py-4 border-t border-slate-800 text-[11px] text-slate-500">
          <div className="flex items-center gap-2">
            <span className="crew-dot"></span>
            <span>System online</span>
          </div>
          <div className="mt-1.5 opacity-70">Phase 5A • Candidate Pipeline</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 lg:pl-64">
        {user?.is_demo && <DemoGuard />}
        <header className="h-16 bg-white/90 border-b border-slate-200 flex items-center justify-between gap-3 px-4 sm:px-6 sticky top-0 z-30 backdrop-blur">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setMenuOpen(true)} className="lg:hidden p-2 -ml-2 rounded-md text-slate-700 hover:bg-slate-100 transition-colors" data-testid="mobile-menu-btn" aria-label="Open menu">
              <Menu className="w-5 h-5" />
            </button>
            <div className="text-xs uppercase tracking-wider text-slate-400 hidden sm:block">Workspace</div>
            <div className="text-sm font-semibold text-slate-800 truncate">Crew Manning Operations</div>
            {user?.is_demo && <DemoBadge />}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="header-user-menu"
                className="flex items-center gap-3 pl-2 pr-3 py-1.5 rounded-full hover:bg-slate-100 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-navy-900 text-white flex items-center justify-center text-xs font-semibold" style={{ background: "#0b192c" }}>
                  {initials || <UserIcon className="w-4 h-4" />}
                </div>
                <div className="text-left leading-tight hidden sm:block">
                  <div className="text-xs font-semibold text-slate-800">{user?.first_name} {user?.last_name}</div>
                  <div className="text-[11px] text-slate-500">{user?.role?.role_name || "—"}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-xs text-slate-500">Signed in as</div>
                <div className="text-sm font-medium">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="profile-change-password-btn" onClick={() => navigate("/profile")}>
                Change password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="header-logout-btn"
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
                className="text-rose-600 focus:text-rose-700"
              >
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
